import { AfterViewInit, Component, ElementRef, OnInit, Output, EventEmitter } from '@angular/core';
import { Feature, Map, Overlay, View } from 'ol';
import TileLayer from 'ol/layer/Tile';
import { TileArcGISRest, XYZ } from 'ol/source';
import { get as getProjection } from 'ol/proj';
import * as Proj from 'ol/proj';
import { defaults as defaultControls } from 'ol/control';
import { Icon, Style, Text } from 'ol/style';
import Stroke from 'ol/style/Stroke';
import VectorSource from 'ol/source/Vector';
import { Polyline } from 'ol/format';
import { environment } from 'src/environments/environment';
import VectorLayer from 'ol/layer/Vector';
import Point from 'ol/geom/Point';
import { TranslateService } from '@ngx-translate/core';
import ContextMenu from 'ol-contextmenu';
import { transform as transform } from 'ol/proj';
import * as olExtent from 'ol/extent';
import Geometry from 'ol/geom/Geometry';

const DEFAULT_ZOOM_LEVEL = 15;
const MAX_ZOOM_LEVEL = 25;
const DEFAULT_EXTENT_PADDING = 30;
const DEFAULT_ANIMATION_MILLISECONDS = 500;
const MAX_RESOLUTION = 0.29858214173896974;
const CLUSTER_PIXEL_DISTANCE = 38;
const OTP_PROJECTION = 'EPSG:4326';

@Component({
  selector: 'dum-ol-map-base',
  templateUrl: './ol-map-base.component.html',
  styleUrls: ['./ol-map-base.component.scss']
})
export class OlMapBaseComponent {
  @Output() eventGetLonLat: EventEmitter<any> = new EventEmitter<any>();

  target: string = 'map-' + Math.random().toString(36).substring(2);
  map: Map;
  overlayPopUp: Overlay;
  containerPopUp;
  contentPopup;

  protected mapEl: HTMLElement;

  protected contextmenu: ContextMenu;
  protected initLayer: VectorLayer;
  protected destLayer: VectorLayer;
  protected routeLayer: VectorLayer;

  //  Variables para el menú contextual en ios
  protected touchX: any;
  protected touchY: any;
  protected touchduration = 500;
  protected threshold = 9;
  protected timer: any;

  constructor(
    protected elementRef: ElementRef,
    protected translateService: TranslateService) {
  }

  protected initMap(
    containerId: string,
    contentId: string,
    lon: number,
    lat: number,
    zoom: number) {
    this.containerPopUp = document.getElementById(containerId);
    this.contentPopup = document.getElementById(contentId);
    this.mapEl = this.elementRef.nativeElement.querySelector('#' + this.target);
    this.setSize();
    this.overlayPopUp = new Overlay({
      element: this.containerPopUp,
      autoPan: true,
      autoPanAnimation: {
        duration: 250,
      },
    });
    this.map = new Map({
      target: this.target,
      layers: [
        new TileLayer({
          source: new XYZ({
            attributions:
              'Tiles © <a href="https://services.arcgisonline.com/ArcGIS/' +
              'rest/services/World_Topo_Map/MapServer">ArcGIS</a>',
            url:
              'https://server.arcgisonline.com/ArcGIS/rest/services/' +
              'World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
          }),
        }),
        /*new TileLayer({
          minResolution: 20,
          source: new TileArcGISRest({
            url: 'https://www10.ava.es/arcgisva/rest/services/CallejeroVA/Accesos_y_alrededores/MapServer',
          })
        }),
        new TileLayer({
          maxResolution: 20,
          source: new TileArcGISRest({
            url: 'https://www10.ava.es/arcgisva/rest/services/CallejeroVA/base/MapServer'
          })
        }),
        new TileLayer({
          source: new TileArcGISRest({
            url: 'https://www10.ava.es/arcgisva/rest/services/CallejeroVA/tematicos/MapServer',
            params: {
              transparent: true,
              layers: 'show:1,3,5,7,9,10,11,12,14,15,16,17,23,24,25,28,29,31,32,33',
              dpi: 96
            }
          })
        })*/
      ],
      overlays: [this.overlayPopUp],
      view: new View({
        projection: getProjection('EPSG:3857'),
        center: Proj.fromLonLat([lon, lat]),
        zoom,
        maxZoom: 19
      }),
      controls: defaultControls({ attribution: false, zoom: false }).extend([]),
    });
    this.createRouteLayers();
    this.addContextMenuToMap();
    this.addMobileContextMenu();
  }

  protected setSize() { }

  protected isClusterFeature(feature: Feature) {
    return feature.get('features') && feature.get('features').length > 1;
  }

  protected getHitCoordinates(coord, geom: Geometry) {
    if (geom.getType() && geom.getType().indexOf('LineString') > -1) {
      return geom.getClosestPoint(coord);
    } else {
      return olExtent.getCenter(geom.getExtent());
    }
  }

  protected addMobileContextMenu() {
    this.map.getViewport().addEventListener('touchstart', this.touchstart, false);
    this.map.getViewport().addEventListener('touchend', this.touchend, false);
    this.map.getViewport().addEventListener('touchmove', this.touchmove, false);
  }

  protected touchstart = (e) => {
    this.timer = setTimeout(() => {
      if (
        (!this.touchX && !this.touchY) ||
        (Math.abs(this.touchX - e.changedTouches[0].clientX) <= this.threshold &&
          Math.abs(this.touchY - e.changedTouches[0].clientY) <= this.threshold)
      ) {
        this.onlongtouch(e);
      }
    }, this.touchduration);
  }

  protected touchmove = (e) => {
    this.touchX = e.changedTouches[0].clientX;
    this.touchY = e.changedTouches[0].clientY;
  }

  protected touchend = () => {
    if (this.timer) {
      clearTimeout(this.timer);
      this.touchX = this.touchY = undefined;
    }
  }

  protected onlongtouch = (e) => {
    const ev = new MouseEvent('contextmenu', {
      clientX: e.changedTouches[0].clientX,
      clientY: e.changedTouches[0].clientY,
    });
    this.map.getViewport().dispatchEvent(ev);
    e.preventDefault();
  }

  protected addContextMenuToMap() {
    if (this.contextmenu) {
      this.map.removeControl(this.contextmenu);
    }
    const contextmenuItems = [
      {
        text: `${this.translateService.instant('GLOBAL.RUTA.INICIO')}`,
        icon: 'assets/imagenes/iconos/flag_origin.png',
        callback: (evnt) => this.setFromContextMenu(true, evnt.coordinate),
      },
      {
        text: `${this.translateService.instant('GLOBAL.RUTA.FIN')}`,
        icon: 'assets/imagenes/iconos/flag_destiny.png',
        callback: (evnt) => this.setFromContextMenu(false, evnt.coordinate),
      }
    ];
    this.contextmenu = new ContextMenu({
      width: 180,
      items: contextmenuItems
    });

    this.contextmenu.on('open', (evt) => {
      this.contextmenu.clear();
      this.contextmenu.extend(contextmenuItems);
      this.contextmenu.extend(this.contextmenu.getDefaultItems());
    });

    this.map.addControl(this.contextmenu);
  }

  private setFromContextMenu(isOrigin: boolean, coordinate: [number, number]) {
    const geoCoordinate = transform(
      coordinate,
      this.map.getView().getProjection(),
      OTP_PROJECTION
    );
    this.eventGetLonLat.emit({
      isOrigin,
      coordinate: Proj.toLonLat(coordinate)
    });
  }

  createRouteLayers() {
    const initFunction = (feature: Feature, resolution) => this.initStyleFuncion(feature, resolution);
    this.initLayer = new VectorLayer({
      source: new VectorSource({
        features: []
      }),
      style: initFunction
    });
    this.map.addLayer(this.initLayer);

    const destFunction = (feature: Feature, resolution) => this.destStyleFuncion(feature, resolution);
    this.destLayer = new VectorLayer({
      source: new VectorSource({
        features: []
      }),
      style: destFunction
    });
    this.map.addLayer(this.destLayer);

    const routeStyleFunction = (feature: Feature, resolution) => this.routeStyleFunction(feature, resolution);
    this.routeLayer = new VectorLayer({
      source: new VectorSource(),
      style: routeStyleFunction,
    });
    this.map.addLayer(this.routeLayer);
  }

  protected initStyleFuncion(feature: Feature, resolution) {
    return new Style({
      image: new Icon({
        anchor: [0.5, 1],
        src: environment.icon_rutas.icon_origen,
        scale: 0.7,
      })
    });
  }

  protected destStyleFuncion(feature: Feature, resolution) {
    return new Style({
      image: new Icon({
        anchor: [0.5, 1],
        src: environment.icon_rutas.icon_destino,
        scale: 0.7,
      })
    });
  }

  protected routeStyleFunction(feature: Feature, resolution) {
    return new Style({
      stroke: new Stroke({
        color: '#1E69DB',
        width: 3,
      })
    });
  }

  public updateRoute(geometryRoute: string,
                     geometryRouteInicio: number[],
                     geometryRouteDestino: number[]) {
    const routeFeat = this.updateRoutePath(geometryRoute);
    const initFeat = this.updateInitPoint(geometryRouteInicio);
    const endFeat = this.updateEndPoint(geometryRouteDestino);
    let extent: olExtent.Extent = olExtent.createEmpty();
    if (routeFeat) {
      extent = olExtent.extend(extent, routeFeat.getGeometry().getExtent());
    }
    if (initFeat) {
      extent = olExtent.extend(extent, initFeat.getGeometry().getExtent());
    }
    if (endFeat) {
      extent = olExtent.extend(extent, endFeat.getGeometry().getExtent());
    }
    if (extent[0] !== Number.POSITIVE_INFINITY) {
      if (!this.isEmptyExtent(extent)) {
        this.fitExtent(extent, {
          duration: DEFAULT_ANIMATION_MILLISECONDS
        }, true);
      }
    }
  }

  protected fitExtent(extent: olExtent.Extent,
                      options = {},
                      isCluster = false) {
    if (extent[0] !== Number.POSITIVE_INFINITY) {
      if ((olExtent.getWidth(extent) === 0) &&
        (olExtent.getHeight(extent) === 0)) {
        this.map.getView().setCenter([extent[0], extent[1]]);
        this.map.getView().setZoom((isCluster) ? MAX_ZOOM_LEVEL : 10 || DEFAULT_ZOOM_LEVEL);
      } else {
        setTimeout(() => {
          this.map.getView().fit(extent,
            {
              ...options,
              ...{
                size: [this.mapEl.clientWidth, this.mapEl.clientHeight],
                padding: [DEFAULT_EXTENT_PADDING,
                  DEFAULT_EXTENT_PADDING,
                  DEFAULT_EXTENT_PADDING,
                  DEFAULT_EXTENT_PADDING]
              }
            });
        }, 0);
      }
    }
  }

  protected isEmptyExtent(extent: olExtent.Extent): boolean {
    const wpixels = olExtent.getWidth(extent) / MAX_RESOLUTION;
    const hpixels = olExtent.getHeight(extent) / MAX_RESOLUTION;
    return (wpixels < CLUSTER_PIXEL_DISTANCE) && (hpixels < CLUSTER_PIXEL_DISTANCE);
  }

  public updateRoutePath(geometryRoute: string) {
    this.routeLayer.getSource().clear();
    if (geometryRoute) {
      const zonaGeom = new Polyline().readGeometry(geometryRoute, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857',
      });
      const zonafeature = new Feature({
        name: 'zona',
        geometry: zonaGeom,
      });
      this.routeLayer.getSource().addFeature(zonafeature);
      return zonafeature;
    }
    return null;
  }

  public updateInitPoint(geometryRouteInicio: number[]) {
    this.initLayer.getSource().clear();
    let initFeat = null;
    if (geometryRouteInicio) {
      initFeat = new Feature({
        geometry: new Point(Proj.fromLonLat(geometryRouteInicio)),
      });
      this.initLayer.getSource().addFeature(initFeat);
    }
    return initFeat;
  }

  public updateEndPoint(geometryRouteDestino) {
    this.destLayer.getSource().clear();
    let endFeat = null;
    if (geometryRouteDestino) {
      endFeat = new Feature({
        geometry: new Point(Proj.fromLonLat(geometryRouteDestino)),
      });
      this.destLayer.getSource().addFeature(endFeat);
    }
    return endFeat;
  }

}
